import ConfigProvider from '@/components/ui/ConfigProvider'
import { themeConfig } from '@/configs/theme.config'
import { MODE_DARK, MODE_LIGHT } from '@/constants/theme.constant'
import useDarkMode from '@/utils/hooks/useDarkMode'
import useLocale from '@/utils/hooks/useLocale'
import useThemeSchema from '@/utils/hooks/useThemeSchema'
import type { CommonProps } from '@/@types/common'

/**
 * Applies the theme to the document and hands it to the UI kit.
 *
 * Must wrap the router: every control in `components/ui` reads its size, mode
 * and direction from the ConfigProvider context this renders, and falls back
 * to hardcoded defaults without it.
 */
const Theme = (props: CommonProps) => {
    useThemeSchema()
    const [isDark] = useDarkMode()
    // Also what writes `lang`/`dir` onto <html> — the attribute Tailwind's
    // `rtl:` variants key off. Calling it here rather than deeper in the tree
    // keeps the document attributes and this context in one render pass.
    const { locale, direction } = useLocale()

    return (
        <ConfigProvider
            value={{
                ...themeConfig,
                // The live values, not the file's. `themeConfig` is the initial
                // state the store was seeded from, so spreading it alone left
                // the UI kit told `mode: 'light'` forever — invisible until
                // something actually reads it, which is the kind of bug that
                // surfaces months later as one mis-coloured control. Direction
                // had the same defect, and it is the more visible one: the kit
                // uses it to decide which side dropdowns, tooltips and input
                // affixes sit on, so a frozen `ltr` leaves Arabic with mirrored
                // text and un-mirrored chrome.
                mode: isDark ? MODE_DARK : MODE_LIGHT,
                direction,
                locale,
            }}
        >
            {props.children}
        </ConfigProvider>
    )
}

export default Theme
